import { useState } from 'react';
import type { PresentationConfig } from '@mem-ai-hub/shared';
import { generatePresentationBlock } from '@/services/documentApi';

export function tryParsePresentation(text: string): PresentationConfig | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return null;

  const tryObject = (raw: string): PresentationConfig | null => {
    try {
      const obj = JSON.parse(raw);
      if (
        obj &&
        typeof obj === 'object' &&
        obj.type === 'presentation' &&
        typeof obj.title === 'string' &&
        typeof obj.fileName === 'string' &&
        Array.isArray(obj.slides) &&
        obj.slides.length > 0
      ) {
        return obj as PresentationConfig;
      }
    } catch {
      // fall through
    }
    return null;
  };

  const direct = tryObject(trimmed);
  if (direct) return direct;

  // Handle trailing chars after the outermost closing brace
  if (trimmed.endsWith('}')) {
    const lastBrace = trimmed.lastIndexOf('}');
    return tryObject(trimmed.slice(0, lastBrace + 1));
  }
  return null;
}

interface PresentationBlockProps {
  config: PresentationConfig;
}

export default function PresentationBlock({ config }: PresentationBlockProps) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slideCount = config.slides.length;

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const result = await generatePresentationBlock(config);
      window.open(result.downloadUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="my-3 rounded-lg border border-border-light bg-bg-secondary p-3">
      <div className="flex flex-wrap items-center gap-3">
        {/* Presentation icon */}
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#E05726]/10">
          <svg className="h-5 w-5 text-[#E05726]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h12a2.25 2.25 0 002.25-2.25V3M3.75 3h16.5M3.75 3H2.25m1.5 0h16.5m0 0h1.5m-3.75 10.5l-3.75-3.75L9 13.5 5.25 9.75M12 16.5v3.75m0 0h-3m3 0h3" />
          </svg>
        </div>

        {/* Title and metadata */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text-primary font-heading">
            {config.title}
          </p>
          <p className="text-xs text-text-secondary">
            {slideCount} {slideCount === 1 ? 'slide' : 'slides'}
          </p>
        </div>

        {/* Download button */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#E05726] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#C04620] disabled:opacity-50"
        >
          {downloading ? (
            <>
              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download .pptx
            </>
          )}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
